<!--div class="row">
  <div class="col">
    <div class="alert alert-warning" role="alert">
      <b>Important Notice:</b> From September 2023 all {{ utils.getAppName() }} users will need to have a unique email in order to sign in.
      If you are currently using a shared email please update it in <a routerLink="/user/details">your profile</a> before September 2023.
      For more information visit the <a [href]="utils.getUrgentNoticeUrl()" target="_blank">{{ utils.getAppName() }} Help Centre</a>.
    </div>
  </div>
</div-->

<!-- Shown to a field worker, meaning a user with no portal feature permission at all. Previously
     keyed off user_account_link.permissions == 'Employee', the legacy role label RBAC retired. -->
<div class="row" appMarginTop *ngIf="hasNoFeaturePermissions">
  <div class="col-12">
    <div class="alert alert-info" role="alert">
      Thank you for using {{ utils.getAppName() }}. The {{ utils.getAppName() }} web portal is for administrative tasks, so there is nothing for you to manage here. You can sign in to {{ utils.getLangTerm('parent-child-sites-combined.plural', 'Sites').toLowerCase() }}, complete inductions and report on the go with the {{ utils.getAppName() }} mobile app. Download it from the <b><a [href]="utils.getAndroidMobileAppStoreUrl()" target="_blank">Google Play Store for Android</a></b> or the <b><a [href]="utils.getIosMobileAppStoreUrl()" target="_blank">App Store for iOS</a></b>.
    </div>
  </div>
</div>

<!-- Site + date-range filter toolbar. Only the premium, site/date-filterable charts use it, so it
     is hidden when none of them are visible (e.g. Free accounts see only the free network chart). -->
<div class="row" appMarginTop *ngIf="canViewSiteAccess || canViewHazards || canViewIncidents">
  <div class="col">
    <button
      mat-raised-button
      color="secondary"
      (click)="onSelectSites()"
      [matBadge]="'' + (app.chart_selected_sites.length ? app.chart_selected_sites.length : 'All')"
      class="m-1 mr-2"
      matTooltip="Filter all results by the selected {{ utils.getLangTerm('parent-child-sites-combined.plural', 'Sites').toLowerCase() }}"
      style="float: left;">
      <mat-icon>domain</mat-icon> Select {{ utils.getLangTerm('parent-child-sites-combined.plural', 'Sites') }}
    </button>

    <input
      type="text"
      placeholder="Select Date Range"
      [(ngModel)]="app.filter_date_range"
      name="filter_date_range"
      class="form-control m-1 mr-2"
      style="min-width: 220px"
      bsDaterangepicker
      [appForceReadonly]="true"
      [bsConfig]="datepickerConfig"
      matTooltip="Filter all results within the selected date range" />

    <button mat-raised-button class="m-1 mr-2" color="secondary" (click)="refreshStats()"
      matTooltip="Refresh the results" style="float: left;">
      <mat-icon>refresh</mat-icon> Refresh
    </button>
  </div>
</div>

<div class="row" appMarginTop>
  <!-- Left column: primary charts. Hidden entirely when the user has none of them, so the right
       column (welcome) fills the space instead of leaving a gap. -->
  <div class="col" *ngIf="canViewSiteAccess || canViewHazards || canViewNetwork">
    <mat-card appearance="outlined" *ngIf="canViewSiteAccess">
      <mat-card-content>
        <h5>All {{ utils.getLangTerm('parent-child-sites-combined.singular', 'Site') }} Activities</h5>
        <div>
          <canvas style="height: 250px !important;" id="site_activities_chart_ref">{{ site_activities_chart }}</canvas>
        </div>
      </mat-card-content>
      <mat-card-actions class="d-flex flex-wrap">
          <button
            mat-raised-button
            class="m-1 mr-2"
            color="secondary"
            [routerLink]="['/', 'reports', 'access-records']"
            matTooltip="Go to the 'Sign In/Out Records' page">
            Sign In/Out Records
          </button>

          <button
            mat-raised-button
            class="m-1 mr-2"
            color="secondary"
            [routerLink]="['/', 'reports', 'onsite-records']"
            matTooltip="Go to the 'Currently Onsite Records' page">
            Currently Onsite Records
          </button>

          <button
            mat-raised-button
            class="m-1 mr-2"
            color="secondary"
            [routerLink]="['/', 'reports', 'time-onsite-records']"
            matTooltip="Go to the 'Time Spent Onsite' page">
            Time Spent Onsite Records
          </button>
      </mat-card-actions>
    </mat-card>

    <mat-card appearance="outlined" *ngIf="canViewHazards">
      <mat-card-content>
        <h5>Hazards & Risks</h5>
        <div>
          <canvas style="height: 250px !important;" id="hazards_chart_ref">{{ hazards_chart }}</canvas>
        </div>
      </mat-card-content>
      <mat-card-actions class="d-flex flex-wrap">
        <button
          mat-raised-button
          class="m-1 mr-2"
          color="secondary"
          [routerLink]="['/', 'hazards']"
          matTooltip="Go to the 'Hazard & Risk Templates' page">
          Hazard & Risk Templates
        </button>
      </mat-card-actions>
    </mat-card>

    <mat-card appearance="outlined" *ngIf="canViewNetwork">
      <mat-card-content>
        <h5>Your Connected Network</h5>
        <div>
          <canvas id="users_chart_ref">{{ users_chart }}</canvas>
        </div>
      </mat-card-content>
      <mat-card-actions class="d-flex flex-wrap">
          <button
            *ngIf="canViewContractors"
            mat-raised-button
            class="m-1 mr-2"
            color="secondary"
            [routerLink]="['/', 'contractors']"
            matTooltip="Go to the '{{ utils.getLangTerm('contractors.plural', 'Contractors') }}' page">
            {{ utils.getLangTerm('contractors.plural', 'Contractors') }}
          </button>

          <button
            *ngIf="canViewEmployees"
            mat-raised-button
            class="m-1 mr-2"
            color="secondary"
            [routerLink]="['/', 'employees']"
            matTooltip="Go to the '{{ utils.getLangTerm('employees.plural', 'Employees') }}'">
            {{ utils.getLangTerm('employees.plural', 'Employees') }}
          </button>
      </mat-card-actions>
    </mat-card>
  </div>

  <div class="col">
    <!-- Hidden for a user with no portal feature permission. They already get the field worker banner
         at the top of this widget, which says the same thing more directly and in the second person.
         Without this the card collapsed to nothing but its heading and a duplicate of that banner,
         and its remaining copy ("Your team can sign in to sites...") addresses an administrator
         managing a team, which such a user is not. -->
    <mat-card appearance="outlined" *ngIf="!hasNoFeaturePermissions">
      <mat-card-content>
        <h5>Welcome to {{ utils.getAppName() }}</h5>

        <!-- Each pointer is gated by a flag pre-computed in ngOnInit() (welcomeCan), never by a
             permission call in the template, so the dashboard does no permission work during change
             detection. hasWelcomeItems suppresses the lead-in sentence and the list together when the
             user can reach none of the destinations. -->

        <!-- Free plan: pointers to the free features available on this account. Each pointer has an
             action-oriented and a read-only variant, chosen by the pre-computed canManage flag, so a
             view-only user is never told to add, complete or respond to anything. -->
        <ng-container *ngIf="!isPremiumPlan && hasWelcomeItems">
          <p *ngIf="hasManageableWelcomeItems">Here are a few things you can do to get started:</p>
          <p *ngIf="!hasManageableWelcomeItems">Here are a few places you can explore:</p>
          <ul>
            <li *ngIf="welcomeCan.employees.visible">
              <ng-container *ngIf="welcomeCan.employees.canManage"><b>Add your {{ utils.getLangTerm('employees.plural', 'Employees').toLowerCase() }}:</b> build your team on the <a routerLink="/employees">{{ utils.getLangTerm('employees.plural', 'Employees') }}</a> page.</ng-container>
              <ng-container *ngIf="!welcomeCan.employees.canManage"><b>Review your {{ utils.getLangTerm('employees.plural', 'Employees').toLowerCase() }}:</b> see your team on the <a routerLink="/employees">{{ utils.getLangTerm('employees.plural', 'Employees') }}</a> page.</ng-container>
            </li>
            <li *ngIf="welcomeCan.tasks.visible">
              <ng-container *ngIf="welcomeCan.tasks.canManage"><b>Assign tasks:</b> create and track work for your team under <a routerLink="/actions">Tasks</a>.</ng-container>
              <ng-container *ngIf="!welcomeCan.tasks.canManage"><b>Follow tasks:</b> keep track of work for your team under <a routerLink="/actions">Tasks</a>.</ng-container>
            </li>
            <li *ngIf="welcomeCan.contractors.visible">
              <ng-container *ngIf="welcomeCan.contractors.canManage"><b>Manage your {{ utils.getLangTerm('contractors.plural', 'Contractors').toLowerCase() }}:</b> keep your <a routerLink="/contractors">{{ utils.getLangTerm('contractors.plural', 'Contractors') }}</a> organised.</ng-container>
              <ng-container *ngIf="!welcomeCan.contractors.canManage"><b>Review your {{ utils.getLangTerm('contractors.plural', 'Contractors').toLowerCase() }}:</b> see your <a routerLink="/contractors">{{ utils.getLangTerm('contractors.plural', 'Contractors') }}</a> and their details.</ng-container>
            </li>
            <li *ngIf="welcomeCan.clientSites.visible">
              <ng-container *ngIf="welcomeCan.clientSites.canManage"><b>Complete {{ utils.getLangTerm('safety-plans.plural', 'Safety Plans').toLowerCase() }} for your clients:</b> work through your <a routerLink="/user/accessible-sites">Client {{ utils.getLangTerm('sites.plural', 'Sites') }}</a>.</ng-container>
              <ng-container *ngIf="!welcomeCan.clientSites.canManage"><b>Check your client work:</b> see the {{ utils.getLangTerm('safety-plans.plural', 'Safety Plans').toLowerCase() }} on your <a routerLink="/user/accessible-sites">Client {{ utils.getLangTerm('sites.plural', 'Sites') }}</a>.</ng-container>
            </li>
            <li *ngIf="welcomeCan.clientPreQualifications.visible">
              <ng-container *ngIf="welcomeCan.clientPreQualifications.canManage"><b>Respond to pre-qualifications:</b> complete requests from your clients under <a routerLink="/my-prequalifications">Client Pre-Qualifications</a>.</ng-container>
              <ng-container *ngIf="!welcomeCan.clientPreQualifications.canManage"><b>Check pre-qualifications:</b> see requests from your clients under <a routerLink="/my-prequalifications">Client Pre-Qualifications</a>.</ng-container>
            </li>
          </ul>
        </ng-container>

        <!-- Premium plan: quick start guide, with the same action-oriented and read-only variants. -->
        <ng-container *ngIf="isPremiumPlan && hasWelcomeItems">
          <p *ngIf="hasManageableWelcomeItems">Quick start guide:</p>
          <p *ngIf="!hasManageableWelcomeItems">A quick tour of your account:</p>
          <ul>
            <li *ngIf="welcomeCan.sites.visible">
              <ng-container *ngIf="welcomeCan.sites.canManage"><b>Prepare a {{ utils.getLangTerm('sites.singular', 'Site').toLowerCase() }}:</b> set up your first <a routerLink="/sites">{{ utils.getLangTerm('sites.singular', 'Site') }}</a>, then add a geofence or QR codes for {{ utils.getLangTerm('sites.singular', 'Site').toLowerCase() }} sign-ins.</ng-container>
              <ng-container *ngIf="!welcomeCan.sites.canManage"><b>Review your {{ utils.getLangTerm('sites.plural', 'Sites').toLowerCase() }}:</b> see your <a routerLink="/sites">{{ utils.getLangTerm('sites.plural', 'Sites') }}</a> and how sign-ins are set up.</ng-container>
            </li>
            <li *ngIf="welcomeCan.employees.visible">
              <ng-container *ngIf="welcomeCan.employees.canManage"><b>Add your {{ utils.getLangTerm('employees.plural', 'Employees').toLowerCase() }}:</b> invite your team on the <a routerLink="/employees">{{ utils.getLangTerm('employees.plural', 'Employees') }}</a> page.</ng-container>
              <ng-container *ngIf="!welcomeCan.employees.canManage"><b>Review your {{ utils.getLangTerm('employees.plural', 'Employees').toLowerCase() }}:</b> see your team on the <a routerLink="/employees">{{ utils.getLangTerm('employees.plural', 'Employees') }}</a> page.</ng-container>
            </li>
            <li *ngIf="welcomeCan.contractors.visible">
              <ng-container *ngIf="welcomeCan.contractors.canManage"><b>Add your {{ utils.getLangTerm('contractors.plural', 'Contractors').toLowerCase() }}:</b> connect your <a routerLink="/contractors">{{ utils.getLangTerm('contractors.plural', 'Contractors') }}</a> and assign them a pre-qualification.</ng-container>
              <ng-container *ngIf="!welcomeCan.contractors.canManage"><b>Review your {{ utils.getLangTerm('contractors.plural', 'Contractors').toLowerCase() }}:</b> see your <a routerLink="/contractors">{{ utils.getLangTerm('contractors.plural', 'Contractors') }}</a> and their pre-qualifications.</ng-container>
            </li>
            <li *ngIf="welcomeCan.accountInductions.visible">
              <ng-container *ngIf="welcomeCan.accountInductions.canManage"><b>Set up inductions:</b> create induction courses under <a routerLink="/courses">Account Inductions</a>; each one is automatically assigned to your connected network.</ng-container>
              <ng-container *ngIf="!welcomeCan.accountInductions.canManage"><b>Review inductions:</b> see the induction courses under <a routerLink="/courses">Account Inductions</a>.</ng-container>
            </li>
            <li *ngIf="welcomeCan.forms.visible">
              <ng-container *ngIf="welcomeCan.forms.canManage"><b>Build forms:</b> create and assign <a routerLink="/dynamic-forms">Forms</a> for your team to complete.</ng-container>
              <ng-container *ngIf="!welcomeCan.forms.canManage"><b>Review forms:</b> see the <a routerLink="/dynamic-forms">Forms</a> assigned to your team.</ng-container>
            </li>
          </ul>
        </ng-container>

        <div class="alert alert-info" role="alert">
          The {{ utils.getAppName() }} web portal is for administrative tasks. Your team can sign in to {{ utils.getLangTerm('parent-child-sites-combined.plural', 'Sites').toLowerCase() }}, complete inductions and report on the go with the {{ utils.getAppName() }} mobile app. Download it from the <b><a [href]="utils.getAndroidMobileAppStoreUrl()" target="_blank">Google Play Store for Android</a></b> or the <b><a [href]="utils.getIosMobileAppStoreUrl()" target="_blank">App Store for iOS</a></b>.
        </div>
      </mat-card-content>
      <mat-card-actions></mat-card-actions>
    </mat-card>

    <mat-card appearance="outlined" *ngIf="canViewIncidents">
      <mat-card-content>
        <h5>Incidents & Near Misses</h5>
        <div>
          <canvas style="height: 250px !important;" id="incidents_chart_ref">{{ incidents_chart }}</canvas>
        </div>
      </mat-card-content>
      <mat-card-actions class="d-flex flex-wrap">
        <button
          mat-raised-button
          class="m-1 mr-2"
          color="secondary"
          [routerLink]="['/', 'incidents']"
          matTooltip="Go to the 'Incident & Near Miss Register' page">
          Incident & Near Miss Register
        </button>
      </mat-card-actions>
    </mat-card>
  </div>
</div>
